import type { FlowNode, SendMsgWxRootConfig } from '@lighthouse/shared'
import { getAllCanBeUpstreamNodes, getUpstreamNodesOptions, PAGE_LINK_OPTION, useAtomData } from '@lighthouse/shared'
import { useNodesData } from '@xyflow/react'
import React, { useCallback, useMemo } from 'react'

import { otherPlatformListAtom } from '@/atoms/application/state'
import { lastPageOfStackAtom } from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'

import * as CM from '../styles'
import { extractDataFn, textRichVariableName } from '../util'
import * as SC from './styles'

type WxRobotCardProps = {
    config: SendMsgWxRootConfig
}

const WxRobotCard: React.FC<WxRobotCardProps> = ({ config }) => {
    const {
        text: { editorContent, mentionedContent: mentionedMobileList }
    } = config
    const otherPlatformList = useAtomData(otherPlatformListAtom)

    const { type } = useFlowContainerContext()
    const isInFlow = !!type
    const isNotAutomation = type !== 'automation'
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const [stackId, pageId] = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => [s?.stackId || '', s?.pageId || ''], [])
    )

    const userOption = useGetUserRecordOptions()
    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })

    const extractDataEditorContent = useMemo(() => extractDataFn(editorContent), [editorContent])

    const extractDataMentionedMobileList = useMemo(() => extractDataFn(mentionedMobileList), [mentionedMobileList])

    const nodesEditorContent = useNodesData(extractDataEditorContent.nodeIds) as FlowNode[]
    const nodesMentionedMobileList = useNodesData(extractDataMentionedMobileList.nodeIds) as FlowNode[]

    const upstreamOptionsEditorContent = useMemo(
        () =>
            isInFlow
                ? getUpstreamNodesOptions({
                      workflowList,
                      dataSourceList,
                      upstreamNodes: getAllCanBeUpstreamNodes(nodesEditorContent || []),
                      allUpstreamNodes: nodesEditorContent
                  })
                : undefined,
        [isInFlow, workflowList, dataSourceList, nodesEditorContent]
    )

    const upstreamOptionsMentionedMobileList = useMemo(
        () =>
            isInFlow
                ? getUpstreamNodesOptions({
                      workflowList,
                      dataSourceList,
                      upstreamNodes: getAllCanBeUpstreamNodes(nodesMentionedMobileList || []),
                      allUpstreamNodes: nodesMentionedMobileList
                  })
                : undefined,
        [isInFlow, workflowList, dataSourceList, nodesMentionedMobileList]
    )

    const textListEditorContent = useMemo(
        () =>
            extractDataEditorContent.objects.map(item => {
                if (item.type === 'text') {
                    return item.value
                }
                return textRichVariableName({
                    userOption,
                    upstreamOptions: upstreamOptionsEditorContent,
                    pageOptions: isNotAutomation ? [PAGE_LINK_OPTION, ...pageOptions] : undefined,
                    data: item
                })
            }),
        [extractDataEditorContent.objects, userOption, upstreamOptionsEditorContent, pageOptions, isNotAutomation]
    )

    const wechatAccount = useMemo(() => otherPlatformList?.find(item => item.type === 'WE_COM_ROBOT'), [otherPlatformList])

    if (!wechatAccount?.configurationComplete) {
        return null
    }

    if (!extractDataEditorContent.objects.length && !extractDataMentionedMobileList.objects.length) {
        return null
    }

    return (
        <CM.CardContainer>
            {extractDataEditorContent.objects.length > 0 && (
                <CM.CardItemContainer>
                    <SC.TypeName>消息内容</SC.TypeName>
                    <CM.FieldItemContent>
                        <CM.FieldItemTitle>
                            {extractDataEditorContent.objects.map(item => {
                                if (item.type === 'text') {
                                    return <SC.Text key={item.value}>{item.value}</SC.Text>
                                }
                                const text = textRichVariableName({
                                    userOption,
                                    upstreamOptions: upstreamOptionsEditorContent,
                                    pageOptions: isNotAutomation ? [PAGE_LINK_OPTION, ...pageOptions] : undefined,
                                    data: item
                                })
                                return (
                                    <SC.Text key={text} color="var(--color-purple-500)">
                                        {text}
                                    </SC.Text>
                                )
                            })}
                        </CM.FieldItemTitle>
                    </CM.FieldItemContent>
                </CM.CardItemContainer>
            )}

            {extractDataMentionedMobileList.objects.length > 0 && (
                <CM.CardItemContainer>
                    <SC.TypeName>提醒人员</SC.TypeName>
                    <CM.FieldItemContent>
                        <CM.FieldItemTitle>
                            {extractDataMentionedMobileList.objects.map(item => {
                                if (item.type === 'text') {
                                    return <SC.Text key={item.value}>{item.value}</SC.Text>
                                }
                                const text = textRichVariableName({
                                    userOption,
                                    upstreamOptions: upstreamOptionsMentionedMobileList,
                                    pageOptions: isNotAutomation ? [PAGE_LINK_OPTION, ...pageOptions] : undefined,
                                    data: item
                                })
                                return (
                                    <SC.Text key={text} color="var(--color-purple-500)">
                                        {text}
                                    </SC.Text>
                                )
                            })}
                        </CM.FieldItemTitle>
                    </CM.FieldItemContent>
                </CM.CardItemContainer>
            )}
        </CM.CardContainer>
    )
}

export default WxRobotCard
